/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, getProductState, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import setPlaceOrderErrorMessage from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetPlaceOrderErrorMessage";
import loadQuoteIfNeeded from "@insite/client-framework/Store/Pages/RfqConfirmation/Handlers/LoadQuoteIfNeeded";
import createOrRequestQuote from "@insite/client-framework/Store/Pages/RfqRequestQuote/Handlers/CreateOrRequestQuote";
import updateQuoteParameter from "@insite/client-framework/Store/Pages/RfqRequestQuote/Handlers/UpdateQuoteParameter";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LoadingOverlayRightStore from "@rightstore/Custom/Components/LoadingOverlayRightStore";
import { datalayerConstants } from "@rightstore/Utilities/constants";
import { datalayerTranaction } from "@rightstore/Utilities/datalayer";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    setOpenModal?: Dispatch<SetStateAction<boolean>>;
    extendedStyles?: ButtonPresentationProps;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);
    const redirectToCheckoutReviewAndSubmitPage = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    const selectedVariant = getProductState(state, state.pages.productDetails.selectedProductId);
    const cart = getCurrentCartState(state).value;
    const cartState = cart?.id ? getCartState(state, cart?.id) : getCurrentCartState(state);
    const { isUpdatingCartLine } = state.pages.cart;
    const { placeOrderErrorMessage } = state.pages.checkoutReviewAndSubmit;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart || isUpdatingCartLine,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        redirectToCheckoutReviewAndSubmitPage,
        quoteProductTypeText: props.productContext.product.properties.quoteProductTypeText,
        isAutoRenewable: selectedVariant.value?.properties?.isAutoRenewable || props.productContext.product.properties.isAutoRenewable,
        rfqQuoteConfirmationPageUrl: getPageLinkByPageType(state, "RfqConfirmationPage")?.url,
        cartState,
        cartId: cart?.id,
        quoteState: getQuoteState(state, state.pages.rfqConfirmation.quoteId),
        isUpdatingCartLinea: isUpdatingCartLine,
        placeOrderErrorMessage
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    updateCartLine: makeHandlerChainAwaitable<Parameters<typeof updateCartLine>[0], CartLineModel>(updateCartLine),
    createOrRequestQuote,
    updateQuoteParameter,
    loadQuoteIfNeeded,
    setPlaceOrderErrorMessage
};

export interface QuoteQuickRequestButtonStyles {
    requestQuoteErrorMessageText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    loader: LoadingSpinnerProps;
    Button?: InjectableCss;
    submitForQuoteButtonTest?: any;
}

export const quoteQuickRequestButtonStyles: QuoteQuickRequestButtonStyles = {
    requestQuoteErrorMessageText: {
        color: "danger",
        css: css`
            margin-top: 6px;
            margin-bottom: 15px;
        `,
    },
    loader: {
        size: 16
    },
    wrapper: {
        css: css`
            button {
                background: ${getColor("info")};
                border: 1px solid ${getColor("info")};
                border-radius: 6px;
                padding: 12px 32px;
                line-height: 0;
                &:focus {
                    outline-color: transparent;
                }
                &:not([disabled]):hover {
                    background: ${getColor("common.background")};
                    border: 1px solid ${getColor("info")};
                    color: ${getColor("info")};
                }
                &:not([disabled]):hover span {
                    color: ${getColor("info")};
                }
            }
        `,
    },
};

const Ownstyles = quoteQuickRequestButtonStyles;

const QuoteQuickRequestButton = ({
    productContext: {
        product: { id: productId },
        productInfo: { qtyOrdered, unitOfMeasure, failedToLoadInventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    updateCartLine,
    canAddToCart,
    labelOverride,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    redirectToCheckoutReviewAndSubmitPage,
    history,
    quoteProductTypeText,
    isAutoRenewable,
    rfqQuoteConfirmationPageUrl,
    cartId,
    createOrRequestQuote,
    updateQuoteParameter,
    loadQuoteIfNeeded,
    setPlaceOrderErrorMessage,
    quoteState,
    isUpdatingCartLinea,
    setOpenModal,
    placeOrderErrorMessage,
    extendedStyles,
    ...otherProps
}: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

    const quote = quoteState.value;
    let viewProductUrl = (
        quote?.quoteLineCollection && quote.quoteLineCollection.length > 0 && quote.quoteLineCollection[0]
    ) ? quote.quoteLineCollection[0].properties["fulfillmentUrl"] : null;

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    useEffect(() => {
        if (viewProductUrl) {
            setIsRedirecting(true);
            window.location.href = viewProductUrl
            viewProductUrl = null;
        }
    }, [quote]);

    useEffect(function updateParameter() {
        updateQuoteParameter({
            quoteId: cartId || "",
            quoteType: "quote",
            jobName: "",
            note: "",
        });
    }, [cartId]);

    const addToCartClickHandler = async () => {
        setOpenModal && setOpenModal(false);
        setIsLoading(true)
        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                    .filter(sectionOptionId => !!sectionOptionId)
                    .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription
        })) as CartLineModel;

        await updateCartLine({
            cartLine: {
                ...cartline,
                properties: {
                    isAutoRenewable: isAutoRenewable ?? "false",
                    queryParams: window.location.search.replace('?', ''),
                    quoteProductTypeText: quoteProductTypeText ?? ""
                }
            }
        });

        createOrRequestQuote({
            onSuccess: (result: { id: any; quoteLineCollection: {
                shortDescription: { erpNumber: { qtyOrdered: any; }[]; qtyOrdered: any; }[];
                erpNumber: { qtyOrdered: any; }[]; qtyOrdered: any; 
}[]; billTo: { id: any; }; currencySymbol: any; }) => {
                setPlaceOrderErrorMessage({});
                loadQuoteIfNeeded({ quoteId: result.id });

                const dataLayerData = {
                    /* eslint-disable */
                    item_id: result.quoteLineCollection && result.quoteLineCollection[0]?.erpNumber,
                    item_name: result.quoteLineCollection && result.quoteLineCollection[0]?.shortDescription,
                    item_brand: datalayerConstants.BRAND,
                    price: 0,
                    quantity: result.quoteLineCollection && result.quoteLineCollection[0]?.qtyOrdered
                }

                datalayerTranaction({
                    eventName: "purchase",
                    transactionId: result.billTo && result.billTo.id,
                    currency: result.currencySymbol && result.currencySymbol,
                    value: "0",
                    data: [dataLayerData]
                })
                setIsLoading(false)
            },
            onError: (result: any) => {
                setPlaceOrderErrorMessage({ errorMessage: result });
                setIsLoading(false)
            }
        });
    };

    return (
        <LoadingOverlayRightStore
            loading={isRedirecting}
            css={css`
                    width: 100%;               
                    height: 100%;
                    position: fixed!important;
                    top: 0;
                    left: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                `}
        >
            <StyledWrapper {...Ownstyles.wrapper}>
                <Button
                    {...extendedStyles}
                    onClick={addToCartClickHandler}
                    disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled || isLoading}
                    {...otherProps}>
                    {isLoading ? <LoadingSpinner {...Ownstyles.loader} /> : labelOverride ?? translate("Request now")}
                </Button>
                {placeOrderErrorMessage && (
                    <Typography {...Ownstyles.requestQuoteErrorMessageText}>{placeOrderErrorMessage}</Typography>
                )}
            </StyledWrapper>
        </LoadingOverlayRightStore>
    );
};

export default withHistory(withProductContext(connect(mapStateToProps, mapDispatchToProps)(QuoteQuickRequestButton)));
